import React, { createContext, useState, useEffect, useCallback } from 'react';
import socket, { initSocket, disconnectSocket } from '../utils/socket';
import { getUserDetailsInLocalStorage, getAuthToken } from '../helpers/UserDetails';
import toast from 'react-hot-toast';
import { mutate } from 'swr';
import { playQROrderSound, playKitchenNewOrderSound, playItemCancelledSound, playKitchenOrderPickupReadySound, unlockAudio } from '../helpers/SoundHelper';

const SocketContext = createContext(null);

const SocketProvider = ({ children }) => {
  const [isSocketConnected, setIsSocketConnected] = useState(socket?.connected || false);

  const authenticateSocket = useCallback(() => {
    const user = getUserDetailsInLocalStorage();
    if (user?.tenant_id) {
      console.log(`[Socket.IO] Authenticating room for tenant_id: ${user.tenant_id}`);
      if (!socket.connected) {
        initSocket();
      }
      socket.emit("authenticate", user.tenant_id);
    } else {
      console.log('[Socket.IO] Authenticate skipped: no user or tenant_id found');
    }
  }, []);

  const printedItemFingerprintsRef = React.useRef(new Set());
  if (typeof window !== 'undefined' && !window.__printedItemFingerprints) {
    window.__printedItemFingerprints = new Set();
  }

  // Global Auto-print KOT handler for orders punched from mobile, QR scanner, or web
  const handleAutoPrintKOT = async (payload) => {
    try {
      if (!payload) return;
      const orderId = payload?.orderId || payload?.order_id || payload?.id;
      if (!orderId) return;

      // Skip if this order was placed locally by the Electron POS itself
      if (window.__lastLocalPlacedOrderId && String(window.__lastLocalPlacedOrderId) === String(orderId)) {
        console.log(`[SocketContext] Order #${orderId} was placed locally on this machine. Skipping auto-print.`);
        return;
      }

      const shouldAutoPrint = localStorage.getItem('kdsAutoPrint') !== 'false';
      if (!shouldAutoPrint) return;

      let items = Array.isArray(payload.items) ? payload.items : [];

      // Fallback: If socket payload did not carry items (e.g. older server event), fetch from orders API
      if (!items || items.length === 0) {
        try {
          const { getOrders } = await import('../controllers/orders.controller');
          const res = await getOrders();
          if (res && res.data && Array.isArray(res.data)) {
            let match = null;
            for (const tg of res.data) {
              if (Array.isArray(tg.orders)) {
                const found = tg.orders.find(o => String(o.id) === String(orderId));
                if (found) {
                  match = found;
                  if (!payload.tableTitle && !payload.table_title) {
                    payload.tableTitle = tg.table_title || '';
                    payload.table_title = tg.table_title || '';
                  }
                  break;
                }
              } else if (String(tg.id) === String(orderId)) {
                match = tg;
                break;
              }
            }
            if (match) {
              if (Array.isArray(match.items)) items = match.items;
              if (!payload.tokenNo && match.token_no) payload.tokenNo = match.token_no;
              if (!payload.waiterName && (match.waiter_name || match.created_by)) payload.waiterName = match.waiter_name || match.created_by;
              if (!payload.deliveryType && match.delivery_type) payload.deliveryType = match.delivery_type;
              if (!payload.customerName && match.customer_name) payload.customerName = match.customer_name;
            }
          }
        } catch (fetchErr) {
          console.warn('[SocketContext] Fallback orders fetch error:', fetchErr.message);
        }
      }

      if (!items || items.length === 0) {
        console.log(`[SocketContext] No items found in payload for Order #${orderId}`);
        return;
      }

      // Deduplicate items using a global and ref set so we never print duplicate KOTs
      const globalSet = window.__printedItemFingerprints || printedItemFingerprintsRef.current;
      const newItemsToPrint = [];

      for (const item of items) {
        if (!item) continue;
        const itemId = item.item_id || item.id || 'item';
        const varId = item.variant_id || 0;
        const qty = item.quantity ?? item.qty ?? 1;
        const notes = item.notes || '';
        const fp = `ord_${orderId}_it_${itemId}_v_${varId}_q_${qty}_n_${notes}`;

        if (!globalSet.has(fp)) {
          globalSet.add(fp);
          printedItemFingerprintsRef.current.add(fp);
          newItemsToPrint.push({
            id: item.id || `${orderId}_${itemId}`,
            item_id: itemId,
            category_id: item.category_id != null ? item.category_id : item.category,
            category: item.category_id != null ? item.category_id : item.category,
            category_printer: item.category_printer || null,
            printer: item.printer || item.category_printer || null,
            title: item.title || item.item_title || 'Item',
            item_title: item.title || item.item_title || 'Item',
            quantity: qty,
            variant: item.variant_title ? { title: item.variant_title } : (item.variant || null),
            variant_title: item.variant_title || item.variant?.title || (typeof item.variant === 'string' ? item.variant : ''),
            addons: item.addons || [],
            notes: notes
          });
        }
      }

      // Prune set if too large
      if (globalSet.size > 2000) {
        const arr = Array.from(globalSet);
        arr.slice(0, 1000).forEach(k => globalSet.delete(k));
      }

      if (newItemsToPrint.length === 0) {
        console.log(`[SocketContext] All items for Order #${orderId} have already been printed. Skipping.`);
        return;
      }

      console.log(`[SocketContext] Auto-printing ${newItemsToPrint.length} new item(s) for Order #${orderId}...`);
      const { groupItemsByPrinter, printKOT } = await import('../helpers/PrinterHelper');
      const pageFormat = localStorage.getItem('page_format') || '80';

      const isRoom = payload.deliveryType === 'room' || payload.delivery_type === 'room' || !!payload.room_name || (payload.source && payload.source.toLowerCase().startsWith('room'));
      const sourceLabel = payload.source || (isRoom ? (payload.room_name ? `Room ${payload.room_name}` : 'Room Order') : (payload.tableTitle || (payload.tableId ? `Table ${payload.tableId}` : '')));

      let categories = [];
      try {
        const cached = localStorage.getItem("cached_categories");
        if (cached) categories = JSON.parse(cached);
      } catch(e) {}

      const printerGroups = groupItemsByPrinter(newItemsToPrint, categories);

      await printKOT(printerGroups, {
        tableId: payload.tableId || payload.table_id || '',
        table_title: sourceLabel,
        deliveryType: payload.deliveryType || payload.delivery_type || (isRoom ? 'room' : ''),
        source: payload.source || '',
        room_name: payload.room_name || '',
        tokenNo: payload.tokenNo || payload.token_no || orderId,
        orderId: orderId,
        customerName: payload.customerName || payload.customer_name || '',
        waiterName: payload.waiterName || payload.waiter_name || '',
        page_format: pageFormat,
        pageFormat: pageFormat
      });
      console.log(`[SocketContext] Successfully sent KOT auto-print for Order #${orderId}`);
    } catch (printErr) {
      console.error('[SocketContext] handleAutoPrintKOT error:', printErr);
    }
  };

  useEffect(() => {
    initSocket();

    // Immediately send auth token to Electron main process so SyncManager
    // connects the cloud socket right away (don't wait for SyncEngine.performSync)
    try {
      const token = getAuthToken();
      if (token && token !== 'null' && token !== 'undefined') {
        let ipc = null;
        if (window.electron && window.electron.ipcRenderer) {
          ipc = window.electron.ipcRenderer;
        } else if (typeof window.require === 'function') {
          ipc = window.require('electron').ipcRenderer;
        }
        if (ipc) {
          ipc.send('set-auth-token', token);
          console.log('[SocketContext] Sent auth token to Electron main process on startup.');
        }
      }
    } catch (e) {}

    if (socket.connected) {
      authenticateSocket();
      setIsSocketConnected(true);
    }

    const onConnect = () => {
      authenticateSocket();
      setIsSocketConnected(true);
    };

    const onDisconnect = () => setIsSocketConnected(false);
    const onConnectError = () => setIsSocketConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);

    // Global call waiter listener
    const handleCallWaiter = (payload) => {
      console.log("[SocketContext] Global Call waiter:", payload);
      unlockAudio();
      const callAudio = new Audio("/new_order_sound.mp3");
      callAudio.play().catch(e => console.log("Audio play failed:", e));
      
      let locationLabel = "Guest";
      if (payload?.roomName) {
        locationLabel = String(payload.roomName).toLowerCase().startsWith("room") ? payload.roomName : `Room ${payload.roomName}`;
      } else if (payload?.tableName) {
        locationLabel = String(payload.tableName).toLowerCase().startsWith("table") || String(payload.tableName).toLowerCase().startsWith("room") ? payload.tableName : `Table ${payload.tableName}`;
      }

      toast(`🛎️ ${locationLabel} is calling for service!`, {
        icon: '🛎️',
        duration: 8000,
        style: {
          borderRadius: '10px',
          background: '#16a34a',
          color: '#fff',
          fontWeight: 'bold',
        },
      });
      
      if ('speechSynthesis' in window) {
        const spoken = `${locationLabel} is calling for service`;
        const utterance = new SpeechSynthesisUtterance(spoken);
        window.speechSynthesis.speak(utterance);
      }
    };
    
    // Global new QR order listener (table scanner or room scanner)
    const handleNewQROrder = (payload) => {
      console.log("[SocketContext] Global new QR order received:", payload);
      unlockAudio();
      const rawTitle = payload?.tableTitle || payload?.table_title;
      const sourceLabel = payload?.source || (payload?.deliveryType === 'room' ? 'Room Scanner' : (rawTitle ? `Table ${rawTitle}` : (payload?.tableId ? `Table ${payload.tableId}` : 'Table Scanner')));
      playQROrderSound({
        source: sourceLabel,
        deliveryType: payload?.deliveryType,
        customerName: payload?.customerName,
        tableId: payload?.tableId,
        tableTitle: rawTitle || null,
        table_title: rawTitle || null,
      });
      handleAutoPrintKOT(payload);
    };

    // Global new general order listener
    const handleGlobalNewOrder = (payload) => {
      console.log("[SocketContext] Global new order event:", payload);
      unlockAudio();
      if (payload?.origin === 'qr_scanner') {
        handleNewQROrder(payload);
      } else {
        if (typeof window !== 'undefined' && window.location.pathname.includes('/kitchen')) {
          playKitchenNewOrderSound(payload);
        }
        handleAutoPrintKOT(payload);
      }
    };

    // Global order update listener
    const handleOrderUpdate = (payload) => {
      console.log("[SocketContext] Global order update received:", payload);
      handleAutoPrintKOT(payload);
    };

    // Global item / order cancellation listener
    const handleGlobalCancel = (payload) => {
      console.log("[SocketContext] Global cancellation alert received:", payload);
      unlockAudio();
      playItemCancelledSound({
        reason: payload?.cancelReason,
        itemTitle: payload?.itemTitle,
        tableTitle: payload?.tableTitle,
      });

      const reasonText = payload?.cancelReason ? `: ${payload.cancelReason}` : '';
      toast(`🚨 Order Item Cancelled${reasonText}`, {
        icon: '⚠️',
        duration: 8000,
        style: {
          borderRadius: '10px',
          background: '#dc2626',
          color: '#ffffff',
          fontWeight: 'bold',
        },
      });
    };

    // Global kitchen order completed listener (plays "Pickup ready for Table X / Room Y" chime & speech alert)
    const handleKitchenOrderCompleted = (payload) => {
      console.log("[SocketContext] Global kitchen order completed alert received:", payload);
      unlockAudio();
      playKitchenOrderPickupReadySound(payload);

      const target = payload?.tableTitle || payload?.table_title || payload?.roomName || (payload?.tokenNo ? `Token #${payload.tokenNo}` : 'Order');
      toast.success(`🔔 Pickup ready: ${target}`, {
        duration: 6000,
        style: {
          borderRadius: '10px',
          background: '#059669',
          color: '#ffffff',
          fontWeight: 'bold',
        },
      });
    };

    socket.on('call_waiter', handleCallWaiter);
    socket.on('new_qrorder', handleNewQROrder);
    socket.on('new_order_notification', handleNewQROrder);
    socket.on('new_order', handleGlobalNewOrder);
    socket.on('order_update', handleOrderUpdate);
    socket.on('order_item_cancelled', handleGlobalCancel);
    socket.on('order_cancelled', handleGlobalCancel);
    socket.on('kitchen_order_completed', handleKitchenOrderCompleted);

    // Respond immediately to login / logout / token update
    const onStorageChange = (e) => {
      if (e.key === 'userDetails') {
        authenticateSocket();
      }
    };
    const onAuthEvent = () => authenticateSocket();

    window.addEventListener('storage', onStorageChange);
    window.addEventListener('auth_state_changed', onAuthEvent);

    // Electron IPC bridge for cloud socket events forwarded from main process
    let removeIpcListener = null;
    if (typeof window !== 'undefined') {
      try {
        let ipc = null;
        if (window.electron && window.electron.ipcRenderer) {
          ipc = window.electron.ipcRenderer;
        } else if (window.require) {
          ipc = window.require('electron').ipcRenderer;
        }

        if (ipc) {
          const onCloudSocketEvent = (event, { eventName, payload }) => {
            console.log(`[Electron IPC Cloud Event] ${eventName}:`, payload);
            if (eventName === 'call_waiter') handleCallWaiter(payload);
            else if (eventName === 'new_qrorder' || eventName === 'new_order_notification') {
              handleNewQROrder(payload);
            } else if (eventName === 'new_order') {
              handleGlobalNewOrder(payload);
            } else if (eventName === 'order_update') {
              handleOrderUpdate(payload);
            } else if (eventName === 'order_item_cancelled' || eventName === 'order_cancelled') {
              handleGlobalCancel(payload);
            } else if (eventName === 'kitchen_order_completed') {
              handleKitchenOrderCompleted(payload);
            }

            // Revalidate all SWR caches so order pages refresh immediately
            if (['new_order', 'new_qrorder', 'new_order_notification', 'order_update', 'order_cancelled', 'order_item_cancelled', 'kitchen_order_completed'].includes(eventName)) {
              setTimeout(() => {
                // Broad revalidation - any key matching these patterns will refetch
                mutate(key => typeof key === 'string' && (
                  key.includes('/orders') || key.includes('/pos') || key.includes('/kitchen')
                ), undefined, { revalidate: true });
              }, 600); // Small delay to let SQLite sync complete first
            }
          };

          ipc.on('cloud-socket-event', onCloudSocketEvent);
          removeIpcListener = () => {
            try { ipc.removeListener('cloud-socket-event', onCloudSocketEvent); } catch(e) {}
          };
        }
      } catch (e) {}
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('call_waiter', handleCallWaiter);
      socket.off('new_qrorder', handleNewQROrder);
      socket.off('new_order_notification', handleNewQROrder);
      socket.off('new_order', handleGlobalNewOrder);
      socket.off('order_update', handleOrderUpdate);
      socket.off('order_item_cancelled', handleGlobalCancel);
      socket.off('order_cancelled', handleGlobalCancel);
      socket.off('kitchen_order_completed', handleKitchenOrderCompleted);
      if (removeIpcListener) removeIpcListener();
      window.removeEventListener('storage', onStorageChange);
      window.removeEventListener('auth_state_changed', onAuthEvent);
    };
  }, [authenticateSocket]);

  return (
    <SocketContext.Provider value={{ socket, isSocketConnected, authenticateSocket, initSocket }}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketContext, SocketProvider };
export default SocketProvider;
